import Shepherd from 'shepherd.js'
import { i18n } from '@/core/i18n'

export type Step = {
  /** Translation key for the title */
  title?: string
  /** Translation key for the body */
  text?: string
  /** Spacing of the highlight around the selected element */
  padding?: number
  /** Shows a Next button */
  showNextButton?: boolean
  /** Show a Finish button */
  showFinishButton?: boolean
  /** Delay before this step is shown. Waited after showOn */
  delay?: number
  /** Query selector for the element to be highlited (if undefined or not found, shows as modal in the center) */
  attachElement?: string
  /** Position of the tutorial in respect to the highlighted element */
  attachPosition?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end'
  /** Query selector for the elements that will advance the tutorial on click */
  nextOnClickElements?: string[]
  /** List of selectors that will have their keydown "Enter" and "Tab" events prevented */
  preventEnterAndTabInElements?: string[]
  /** List of selectors that will have their "click" events prevented */
  preventClickInElements?: string[]
  /** List of selectors that will have their "focus out" events prevented */
  preventFocusOutInElements?: string[]
  /** List of selectors that will trigger a refresh of the step on "input" and "change" */
  watchValueOfElements?: string[]
  /** List of selectors that need to exist before the modal will show (the attachElement is already awaited).
   * Runs after showOn and the delay */
  waitForElements?: string[]
  /** List of selectors that will be visually highlighted.
   * Issue: if the element is "display: inline" the highlight will not work in Firefox
   */
  highlightElements?: string[]
  /** List of selectors that will be visually highlighted. This highlight doesn't extend beyond the element, good for dropdown items */
  highlightInternalElements?: string[]
  /** Function that determines if the step should be shown (return true) or skipped (return false).
   * Runs before anything else */
  showOn?: () => boolean
  /** Callback to be run before the step is shown.
   * Runs after showOn, the delay and waitForElements */
  beforeShow?: () => void
  /** Function that determines if the tour can progress to the next step or not.
   * Runs after show, every time a change is detected in the HTML of the attached element,
   * every time the value of an watchValueOfElements changes ("input" and "change" events)
   * and every time a nextOnClickElements has been clicked */
  canGoToNext?: () => boolean
}

export type Config = {
  steps: Step[]
  onStart?: () => void
}

function canGoToNext(step: Step) {
  if (step.canGoToNext) {
    return step.canGoToNext()
  }

  return true
}

function getAttachTo(step: Step) {
  if (step.attachElement) {
    return {
      element: step.attachElement,
      on: step.attachPosition,
    }
  }
}

function getClasses(step: Step) {
  switch (step.attachPosition) {
    case 'left':
      return 'margin-left'
    case 'right':
      return 'margin-right'
    case 'bottom':
      return 'margin-top'
    case 'top':
      return 'margin-bottom'
  }
}

function getButtons(tour: Shepherd.Tour, step: Step) {
  const allButtons = [
    {
      isVisible: step.showNextButton,
      button: {
        text: i18n.global.t('cfc.modal.tutorial.welcome.next'),
        disabled: () => !canGoToNext(step),
        action: () => {
          if (canGoToNext(step)) {
            tour.next()
          }
        },
      },
    },
    {
      isVisible: step.showFinishButton,
      button: {
        text: i18n.global.t('cfc.modal.tutorial.welcome.finished'),
        disabled: () => !canGoToNext(step),
        action: () => {
          if (canGoToNext(step)) {
            tour.complete()
          }
        },
      },
    },
  ]

  const buttons = allButtons.filter((el) => el.isVisible).map((el) => el.button)

  return buttons
}

function waitDelay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * @throws Element not found within specified timeout
 */
function waitForElement(selector: string, timeout = 40_000) {
  return new Promise<HTMLElement>((resolve, reject) => {
    const element = document.querySelector<HTMLElement>(selector)

    if (element) {
      resolve(element)

      return
    }

    const observer = new MutationObserver((mutationsList, observer) => {
      const el = document.querySelector<HTMLElement>(selector)

      if (el) {
        observer.disconnect()

        resolve(el)
      }
    })

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
    })

    setTimeout(() => {
      observer.disconnect()

      reject(new Error(`Element ${selector} not found within ${timeout}ms`))
    }, timeout)
  })
}

/**
 * First it will wait for the specified step delay (if set), and then it will wait for the step attachElement to be visible (timeout in 3s)
 */
function getBeforeShowPromise(step: Step) {
  return async () => {
    if (step.delay) {
      await waitDelay(step.delay)
    }

    if (step.waitForElements) {
      for (const element of step.waitForElements) {
        await waitForElement(element)
      }
    }

    if (step.attachElement) {
      await waitForElement(step.attachElement)
    }
  }
}

function getWhen(tour: Shepherd.Tour, step: Step) {
  async function refreshStep() {
    // Postpone the refresh by pushing it to the event loop queue, so Vue can finish updating the DOM
    await waitDelay(0)

    // This will trigger a re-computation and re-render of the step (updating the texts, disabled buttons, ...)
    tour.getCurrentStep()?.updateStepOptions({})
  }

  function forEachHtmlElement(
    selectors: string[] | undefined,
    callback: (element: HTMLElement) => void
  ) {
    selectors?.forEach((selector) =>
      document.querySelectorAll<HTMLElement>(selector).forEach(callback)
    )
  }

  // Watch input events for validations

  function addWatchValueEvents() {
    forEachHtmlElement(step.watchValueOfElements, (element) => {
      element.addEventListener('input', refreshStep)
      element.addEventListener('change', refreshStep)
    })
  }

  function removeWatchValueEvents() {
    forEachHtmlElement(step.watchValueOfElements, (element) => {
      element.removeEventListener('input', refreshStep)
      element.addEventListener('change', refreshStep)
    })
  }

  // Click events to go to the next step

  function goToNext() {
    if (canGoToNext(step)) {
      tour.next()
    }
  }

  function addNextOnClickEvents() {
    forEachHtmlElement(step.nextOnClickElements, (element) => {
      element.addEventListener('click', goToNext, { capture: true })
    })
  }

  function removeNextOnClickEvents() {
    forEachHtmlElement(step.nextOnClickElements, (element) => {
      element.removeEventListener('click', goToNext, { capture: true })
    })
  }

  // Prevent Enter and Tab events

  function preventEnterAndTab(event: KeyboardEvent) {
    // Avoid form submissions on "Enter"
    if (event.key === 'Enter') {
      event.preventDefault()
    }

    // Avoid focusing other elements on "Tab"
    if (event.key === 'Tab') {
      event.preventDefault()
    }
  }

  function addPreventEnterAndTabEvents() {
    const addEventListener = (element: HTMLElement) =>
      element.addEventListener('keydown', preventEnterAndTab)

    forEachHtmlElement(step.preventEnterAndTabInElements, addEventListener)

    if (step.attachElement) {
      forEachHtmlElement([step.attachElement], addEventListener)
    }
  }

  function removePreventEnterAndTabEvents() {
    const removeEventListener = (element: HTMLElement) =>
      element.removeEventListener('keydown', preventEnterAndTab)

    forEachHtmlElement(step.preventEnterAndTabInElements, removeEventListener)

    if (step.attachElement) {
      forEachHtmlElement([step.attachElement], removeEventListener)
    }
  }

  // Prevent Click events

  function preventClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  function addPreventClickEvents() {
    forEachHtmlElement(step.preventClickInElements, (element) => {
      element.addEventListener('click', preventClick, { capture: true })
      element.addEventListener('mousedown', preventClick, { capture: true })
    })
  }

  function removePreventClickEvents() {
    forEachHtmlElement(step.preventClickInElements, (element) => {
      element.removeEventListener('click', preventClick, { capture: true })
      element.removeEventListener('mousedown', preventClick, { capture: true })
    })
  }

  // Prevent Focus out events

  function preventFocusOut(event: FocusEvent) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  function addPreventFocusOutEvents() {
    forEachHtmlElement(step.preventFocusOutInElements, (element) => {
      element.addEventListener('focusout', preventFocusOut, { capture: true })
      element.addEventListener('blur', preventFocusOut, { capture: true })
    })
  }

  function removePreventFocusOutEvents() {
    forEachHtmlElement(step.preventFocusOutInElements, (element) => {
      element.removeEventListener('focusout', preventFocusOut, {
        capture: true,
      })
      element.removeEventListener('blur', preventFocusOut, { capture: true })
    })
  }

  // Highlight elements

  const highlightClassName = 'tutorial-highlight'

  function addHighlights() {
    forEachHtmlElement(step.highlightElements, (element) =>
      element.classList.add(highlightClassName)
    )
  }

  function removeHighlights() {
    forEachHtmlElement(step.highlightElements, (element) =>
      element.classList.remove(highlightClassName)
    )
  }

  // Highlight internal elements

  const highlightInternalClassName = 'tutorial-highlight-internal'

  function addInternalHighlights() {
    forEachHtmlElement(step.highlightInternalElements, (element) =>
      element.classList.add(highlightInternalClassName)
    )
  }

  function removeInternalHighlights() {
    forEachHtmlElement(step.highlightInternalElements, (element) =>
      element.classList.remove(highlightInternalClassName)
    )
  }

  // Observer

  function addTutorialEffects() {
    addWatchValueEvents()
    addNextOnClickEvents()
    addPreventEnterAndTabEvents()
    addPreventClickEvents()
    addPreventFocusOutEvents()
    addHighlights()
    addInternalHighlights()
  }

  function removeTutorialEffects() {
    removeWatchValueEvents()
    removeNextOnClickEvents()
    removePreventEnterAndTabEvents()
    removePreventClickEvents()
    removePreventFocusOutEvents()
    removeHighlights()
    removeInternalHighlights()
  }

  async function onChange() {
    // Refresh Shepherd element
    refreshStep()

    // Re-apply tutorial effects, so new elements also get them
    removeTutorialEffects()
    addTutorialEffects()
  }

  const mutationObserver = new MutationObserver(onChange)

  return {
    'before-show'() {
      step.beforeShow?.()
    },
    show() {
      // Avoid Shepherd stealing the focus from input elements
      // by disabling it's "focus" function, which is triggered
      // on resize and scroll (especially annoying in Cypress)
      // Keep an eye on this issue: https://github.com/shipshapecode/shepherd/issues/1143

      const shepherdElement = tour.getCurrentStep()?.getElement()

      if (shepherdElement) {
        // Make "focus" do nothing
        shepherdElement.focus = () => {}
      }

      addTutorialEffects()

      const targetElement = tour.getCurrentStep()?.getTarget()

      if (targetElement) {
        mutationObserver.observe(targetElement, {
          subtree: true,
          childList: true,
          // attributes: true,
        })
      }
    },
    hide: () => {
      removeTutorialEffects()

      mutationObserver.disconnect()
    },
  }
}

function getTourStep(tour: Shepherd.Tour, step: Step, index: number) {
  const tourStep = {
    id: 'step' + index,
    title: () => i18n.global.t(step.title ?? ''),
    text: () => i18n.global.t(step.text ?? ''),
    modalOverlayOpeningRadius: 10,
    modalOverlayOpeningPadding: step.padding ?? 10,
    attachTo: getAttachTo(step),
    classes: getClasses(step),
    buttons: getButtons(tour, step),
    beforeShowPromise: getBeforeShowPromise(step),
    when: getWhen(tour, step),
    showOn: step.showOn,
  }

  return tourStep
}

export function createTour(config: Config) {
  const tour = new Shepherd.Tour({
    modalContainer: undefined,
    stepsContainer: undefined,
    useModalOverlay: true,
    keyboardNavigation: false,
    defaultStepOptions: {
      classes: 'shepherd-cfc',
      scrollTo: true,
      scrollToHandler: (element) => {
        setTimeout(
          () =>
            element?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            }),
          100
        )
      },
      cancelIcon: {
        enabled: true,
      },
    },
  })

  config.steps.forEach((step, index) => {
    const tourStep = getTourStep(tour, step, index)

    tour.addStep(tourStep)
  })

  if (config.onStart) {
    tour.on('start', config.onStart)
  }

  // When the tutorial is closed (with the X or by completing it)
  // remove all the effects that were applied by the tutorial (by triggering the "hide" listener)

  const triggerHide = () => {
    const step = tour.getCurrentStep()

    if (step) {
      step.options?.when?.hide?.call(step)
    }
  }

  tour.on('cancel', triggerHide)

  tour.on('complete', triggerHide)

  return tour
}
